import { useState, useEffect } from "react";
import { avatarBg } from "@/lib/avatar";
import { useNavigate } from "react-router-dom";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { useLocale } from "@/i18n/LocaleContext";
import { toast } from "sonner";
import { Heart, MapPin, BadgeCheck } from "lucide-react";
import Navigation from "@/components/Navigation";

export default function Favorites() {
  const navigate = useNavigate();
  const { t } = useLocale();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchFavorites = async () => {
    try {
      const response = await api.get("/favorites");
      setProfiles(response.data.profiles);
    } catch (error) {
      toast.error("Failed to load favorites");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFavorites();
  }, []);

  const handleRemove = async (e, userId) => {
    e.stopPropagation();
    try {
      await api.post(`/favorites/${userId}`);
      setProfiles((prev) => prev.filter((p) => p.user_id !== userId));
      toast.success("Removed from favorites");
    } catch (error) {
      toast.error("Failed to remove");
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="favorites-page">
      <Navigation />
      <main className="pt-20 pb-24 px-4 md:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold font-['Outfit'] tracking-tight flex items-center gap-3">
              <Heart className="w-7 h-7 text-primary fill-primary" />
              {t("favorites.title")}
            </h1>
            <p className="text-muted-foreground mt-1">{t("favorites.subtitle")}</p>
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : profiles.length === 0 ? (
            <div className="glass-card p-12 text-center" data-testid="favorites-empty">
              <Heart className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">{t("favorites.none")}</h2>
              <p className="text-muted-foreground mb-6">
                {t("favorites.noneDesc")}
              </p>
              <Button onClick={() => navigate("/discover")} data-testid="favorites-discover-btn">
                {t("favorites.browse")}
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {profiles.map((profile) => (
                <div
                  key={profile.user_id}
                  className="relative rounded-2xl overflow-hidden border border-white/10 cursor-pointer group"
                  onClick={() => navigate(`/profile/${profile.user_id}`)}
                  data-testid="favorite-card"
                >
                  <div className="aspect-[3/4]">
                    <img
                      src={profile.photos?.[0] || profile.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(profile.name)}&background=${avatarBg()}&color=fff&size=400`}
                      alt={profile.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                  <button
                    type="button"
                    className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center hover:bg-black/70 transition-colors"
                    onClick={(e) => handleRemove(e, profile.user_id)}
                    title="Remove from favorites"
                    data-testid="remove-favorite-btn"
                  >
                    <Heart className="w-5 h-5 text-primary fill-primary" />
                  </button>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <p className="font-semibold flex items-center gap-1.5">
                      {profile.name}{profile.age ? `, ${profile.age}` : ""}
                      {profile.is_verified && <BadgeCheck className="w-4 h-4 text-primary" />}
                    </p>
                    {profile.location && (
                      <p className="text-xs text-white/70 flex items-center gap-1 mt-0.5">
                        <MapPin className="w-3 h-3" /> {profile.location}
                      </p>
                    )}
                    {profile.ratings_enabled !== false && profile.review_count > 0 && (
                      <p className="text-xs text-amber-400 mt-0.5">
                        ★ {Number(profile.avg_rating).toFixed(1)} ({profile.review_count})
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
